<template>
  <div v-if="data && !isLoadingOrder" class="section-summary">
    <template v-if="isVip()">
      <div class="vip-confirm-container">
        <div class="vip-confirm-image">
          <img
            :src="resizeImage(data?.product_main?.image_url, 640)"
            alt="product image"
            class="image"
          />
        </div>
        <div class="vip-confirm-product-name">
          {{ data.product_main.name || 'ไม่พบข้อมูล' }}
        </div>
      </div>
    </template>
    <template v-else>
      <CheckoutProductItems
        :isDefault="false"
        :data="productSummaryItems"
        :branch-type="branchType"
        :cart-delivery="cartDelivery"
        class="mb-4"
      />
      <CheckoutSummary
        :isDefault="false"
        :cartSummaryData="cartSummaryData"
        :isDeposit="isDeposit"
        :isPickupAtStore="!isDelivery"
        :shipDate="shippingDate"
      />
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { resizeImage } from '@/utils/helpers'
import isBnn from '@/config/isBnn'
import colorBnn from '@/config/color'
import branchTypes from '@/config/branchTypes'
import {
  ORDER_STATUS,
  CONFIRM_PAYMENT_ORDER_TYPE,
} from '@/config/order'

export default {
  name: 'CheckoutModalConfirmOrderContent',

  inject: ['isVip'],

  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },

  components: {
    CheckoutProductItems: () =>
      import('@/components/studio7/CheckoutProductItems'),
    CheckoutSummary: () =>
      import('@/components/studio7/CheckoutSummary'),
  },

  data() {
    return {
      // Import
      colorBnn,
      isBnn,
      ORDER_STATUS,

      // Data
      form: {
        couponCode: '',
      },
      freebieListByCouponModal: false,
    }
  },

  computed: {
    ...mapState({
      selectSKU: state => state.model.selectSKU,
      bundleList: state => state.model.bundleList,
      campaign: state => state.campaign.current,

      //   data: state => state.order.data,
      isLoadingOrder: state => state.order.isLoading,
    }),

    isDelivery() {
      return this.data?.is_delivery
    },

    isDeposit() {
      return this.data?.is_deposit
    },

    // props for <CheckoutProductItems> derived from the order payload
    branchType() {
      return this.data?.is_deposit
        ? branchTypes.BRANCH_TYPE_DEPOSIT
        : branchTypes.BRANCH_TYPE_FULL_PAYMENT
    },

    cartDelivery() {
      return this.data?.is_delivery ? 'delivery' : 'store'
    },

    isPayAtStore() {
      return this.data?.is_pay_at_store
    },

    isPayAtStorePromotion() {
      return (
        this.data?.pay_at_store_detail?.order_type ==
        CONFIRM_PAYMENT_ORDER_TYPE.WITH_PLAN
      )
    },

    ...mapGetters({
      additionalProduct: 'model/getAdditionalProduct',
      appleCare: 'model/getAppleCare',
      cartItems: 'cart/cartItems',
      productItem: 'cart/productItem',
      priceSummary: 'cart/priceSummary',
      isSoldOut: 'model/getSoldOut',
      bundleItems: 'cart/bundleItems',
      discountOnTop: 'cart/discountOnTop',
      deliveryItems: 'cart/deliveryItems',
      getBundleDelivery: 'model/getBundleDelivery',
      accessoryBundles: 'model/getAccessoryBundles',
      // getFreebieHandraiser
      handraiserFreebies: 'model/getHandraiserFreebies' ?? [],
      productPaymentOptions: 'paymentType/getProductPaymentOptions',
    }),

    finalPrice() {
      return (
        this.data?.pay_at_store_detail?.paid_amount ||
        this.data?.total_price ||
        0
      )
    },

    additionalDiscountLabel() {
      return this.isPayAtStorePromotion
        ? 'ส่วนลดจากโปรโมชั่นเบอร์ทรู'
        : 'ส่วนลดเพิ่มเติม'
    },

    cartSummaryData() {
      return {
        before_discount: this.data?.before_discount,
        summary_discount: Number(this.data?.discount),
        product_price_deposit_summary: this.data?.deposit,
        remain_amount_original: this.data?.remaining,
        final_price: this.finalPrice,

        // Additional discount
        additional_discount: this.isPayAtStorePromotion
          ? this.data?.pay_at_store_detail
              ?.with_plan_promotion_discount
          : Number(this.data?.pay_at_store_detail?.voucher_amount) ??
            0,

        advance_payment: this.data?.pay_at_store_detail
          ?.with_plan_advance_payment,
        advance_payment_optional: this.data?.pay_at_store_detail
          ?.with_plan_advance_payment_optional,

        guarantee_amount: this.data?.guarantee_amount ?? 0,

        coupons: this.data?.coupons ?? [],
        discount_coupon: this.data?.discount_coupon ?? 0,
      }
    },

    bundles() {
      return (
        this.data?.bundle_products?.filter(
          i => i?.type !== 'delivery',
        ) ?? []
      )
    },

    receiveBranchName() {
      return this.data?.receive_branch_name ?? ''
    },

    receiveBranchAddress() {
      return this.data?.receive_branch_address
        ? `${this.data?.receive_branch_address?.address} ${this.data?.receive_branch_address?.province} `
        : ''
    },

    shippingDate() {
      return (
        this.productPaymentOptions?.message ||
        this.data?.shipping_date ||
        this.data?.receive_date ||
        'ไม่พบข้อมูล'
      )
    },

    shipDate() {
      return (
        this.selectSKU.shipping_date ||
        this.campaign?.data?.shipdate ||
        '-'
      )
    },

    fullAddress() {
      return `เลขที่ ${this.data?.address} แขวง/ตำบล ${this.data?.sub_district} เขต/อำเภอ ${this.data?.district} ${this.data?.province} ${this.data?.postcode}`
    },

    fullTaxAddress() {
      return `เลขที่ ${this.data?.tax_address?.address} แขวง/ตำบล ${this.data?.tax_address?.sub_district} เขต/อำเภอ ${this.data?.tax_address?.district} ${this.data?.tax_address?.province} ${this.data?.tax_address?.postcode}`
    },

    fullBillAddress() {
      return `เลขที่ ${this.data?.bill_address?.address} แขวง/ตำบล ${this.data?.bill_address?.sub_district} เขต/อำเภอ ${this.data?.bill_address?.district} ${this.data?.bill_address?.province} ${this.data?.bill_address?.postcode}`
    },

    depositNumber() {
      return this.data?.deposit_number ?? 'กำลังดำเนินการ'
    },

    // main product row + bundles, shared by every <CheckoutProductItems>
    productSummaryItems() {
      return [
        {
          imageUrl: this.data?.product_main?.image_url,
          name: this.data?.product_main?.name,
          price_ship: this.data?.product_main?.price,
          price_store: this.data?.product_main?.price,
          price_deposit: this.data?.product_main?.price,
          final_bundle_price: this.data?.product_main?.price,
        },
        ...this.bundles,
      ]
    },
  },

  async mounted() {},

  methods: {
    resizeImage(imageUrl, size = 300) {
      return resizeImage(imageUrl, size)
    },

    contactUs() {
      // TODO: Redirect to contact page new page
      window.open(
        'https://www.studio7thailand.com/th/pages/contact-us',
        '_blank',
      )
    },

    goBack() {
      let slug =
        this.$route.params?.slug ||
        localStorage.getItem('productLatestVisit')

      this.$router.push({
        name: 'ProductDetail',
        params: {
          slug: slug,
        },
      })
    },
  },
}
</script>

<style lang="stylus" scoped>
.order-detail-header{
    display: flex;
    align-items: center;
    gap: 16px;
}

.order-detail-container {
    border: 1px solid $color-gray85
    border-radius: 8px
    padding: 16px

    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 16px

    @media screen and (min-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
    }

    .span-2 {
        grid-column: span 1

        @media screen and (min-width: 1024px) {
            grid-column: span 2
        }
    }
}

.order-detail-action {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
}

.section-summary-sticky {
    background-color: $color-grey-light-80;

    grid-column: span 2;
}

.section-summary-sticky-accordion {
    width 100%
    max-width: 100dvw
    padding: 21px 21px 0;

    display flex;
    flex-direction: column;
    gap: 21px;
}

.full-page
 overflow: visible !important


.action-confirm-order-container {
    position sticky
    bottom 0
    width 100%
    background linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 50%);
    padding 24px 16px

    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    flex-direction: column;

    .ship-date {
        width 100%
        text-align: center;
        font-weight: 500;
        font-size: 16px;
        padding: 80px
        background-color: $color-grey-light-80
        border-radius: 8px
    }
}

.vip-confirm-container
	display: flex
	flex-direction: column
	gap: 16px
	.vip-confirm-image
		position relative
		width 100%
		height auto
		max-height 280px
		min-height 280px
		flex-basis 100%
		border-radius 5px
		border none
		margin 0 auto
		aspect-ratio 1 / 1
		.image
			width 100%
			height 100%
			object-fit contain
			object-position center
			border-radius 5px

	.vip-confirm-product-name
		font-size 20px
		font-weight 600
		text-align center
</style>
